"use client";

import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Eye, EyeOff, HelpCircle, Lock, Mail } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { validateLoginInput } from "@/config/auth/business-rules";
import {
  getVerifiedLoginDestination,
  signInWithIsolatedSession,
} from "@/lib/auth/password-login";
import { translateAuthMessage } from "@workspace/core/authuser/auth-messages";
import { trackEvent } from "@/lib/analytics/client";

const MAX_FAILED_ATTEMPTS = 5;
const LOCK_TIME_MS = 2 * 60 * 1000;

type FormFieldErrors = {
  email?: string;
  password?: string;
};

const LoginPage: React.FC<{
  redirectTo: string;
  allowedOrigins: string[];
}> = ({ redirectTo, allowedOrigins }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FormFieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockRemainingSeconds, setLockRemainingSeconds] = useState(0);

  const router = useRouter();

  const handleBackClick = () => {
    router.push("/");
  };

  const handleSignupClick = () => {
    // O cadastro cria contas do web; um destino em outro app (admin) nao faz
    // sentido para quem acabou de se cadastrar.
    const signupRedirect = redirectTo.startsWith("/") ? redirectTo : "";
    router.push(
      signupRedirect
        ? `/auth/signup?redirectTo=${encodeURIComponent(signupRedirect)}`
        : "/auth/signup",
    );
  };

  const handleLoginMigrationSuccess = () => {
    const normalizedEmail = email.trim().toLowerCase();
    const query = normalizedEmail
      ? `?email=${encodeURIComponent(normalizedEmail)}`
      : "";

    router.push(`/auth/migration-success${query}`);
  };

  useEffect(() => {
    if (!lockedUntil) {
      setLockRemainingSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const remainingMs = lockedUntil - Date.now();
      if (remainingMs <= 0) {
        setLockedUntil(null);
        setFailedAttempts(0);
        setLockRemainingSeconds(0);
        return;
      }
      setLockRemainingSeconds(Math.ceil(remainingMs / 1000));
    };

    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [lockedUntil]);

  const isLocked = useMemo(
    () => lockRemainingSeconds > 0,
    [lockRemainingSeconds],
  );

  const registerFailedAttempt = () => {
    setFailedAttempts((prevAttempts) => {
      const nextAttempts = prevAttempts + 1;
      if (nextAttempts >= MAX_FAILED_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCK_TIME_MS);
        return 0;
      }
      return nextAttempts;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setGeneralError(null);
    const inputErrors = validateLoginInput({ email, password });
    setFieldErrors(inputErrors);

    if (Object.keys(inputErrors).length > 0) {
      return;
    }

    if (isLocked) {
      setGeneralError(
        `Muitas tentativas. Tente novamente em ${lockRemainingSeconds}s.`,
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await signInWithIsolatedSession(email, password);

      if (response.status === "OK") {
        setFieldErrors({});
        setGeneralError(null);
        setFailedAttempts(0);
        setLockedUntil(null);
        // Enviado antes do redirect de pagina inteira. O gtag usa sendBeacon,
        // entao o evento sobrevive a navegacao.
        trackEvent("login", { method: "password" });
        const destination = await getVerifiedLoginDestination(
          response.user.id,
          redirectTo,
          allowedOrigins,
        );
        window.location.replace(destination);
        return;
      }

      if (response.status === "FIELD_ERROR") {
        const nextErrors: FormFieldErrors = {};
        for (const formField of response.formFields) {
          if (formField.id === "email" || formField.id === "password") {
            nextErrors[formField.id] = formField.error;
          }
        }
        setFieldErrors(nextErrors);
        setGeneralError("Revise os campos destacados.");
        return;
      }

      if (response.status === "WRONG_CREDENTIALS_ERROR") {
        registerFailedAttempt();
        setGeneralError("E-mail ou senha inválidos.");
        return;
      }

      if (response.status === "SIGN_IN_NOT_ALLOWED") {
        setGeneralError(
          translateAuthMessage(
            response.reason,
            "Seu login não está autorizado.",
          ),
        );
        return;
      }

      setGeneralError("Não foi possível concluir o login. Tente novamente.");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // So o sucesso vai para a tela de migracao. A falha ia para la tambem, e
      // mandava o usuario conferir um e-mail que nunca foi enviado — ele ficava
      // esperando por algo que nao viria, sem saber que precisava do suporte.
      if (error.message === "USER_MIGRATED_SUCCESSFULLY") {
        setFieldErrors({});
        setGeneralError(null);
        setFailedAttempts(0);
        setLockedUntil(null);
        handleLoginMigrationSuccess();
        return;
      }
      // Sinalizacoes de migracao que nao sao o caminho feliz (por exemplo
      // MIGRATION_LOOKUP_FAILED) chegavam aqui e viravam a mensagem de sessao,
      // que nao descreve o que aconteceu. Agora recebem o texto proprio.
      setGeneralError(
        translateAuthMessage(
          error?.message,
          "Não foi possível confirmar sua sessão com segurança. Tente entrar novamente.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch overflow-hidden font-display bg-background-light dark:bg-background-dark">
      {/* Left Side: Visual/Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0">
          <a
            href="https://lh3.googleusercontent.com/aida-public/AB6AXuCP4FWL3IimJFzeMgVolKWv4PHmd4XAjtn2I4jFRrVNQ7qmhAX-SxGK23oagao0ckbDoG56gsM39yPWNXPs_75dIFkaD6gN-RU_A6pSMdzdE4xMzt6Cnc5d_St6sUjaIej2gL9SLyjkrQodc8hYJFhGIMGSBcUVFQ1pEDMXcIC7pvqphasD0K1uCpg_NbWMMXMnkK4fgU_SX2jkorvfOGwj3q9v-qpNMv8i2vcDIfPlaSqW1siJ2klquGH4vuAba6J17rgebhWXgao"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              alt="Surveyor using high-tech equipment"
              className="w-full h-full object-cover opacity-60 mix-blend-luminosity cursor-zoom-in"
              src="https://lh3.googleusercontent.com/aida-public/AB6AXuCP4FWL3IimJFzeMgVolKWv4PHmd4XAjtn2I4jFRrVNQ7qmhAX-SxGK23oagao0ckbDoG56gsM39yPWNXPs_75dIFkaD6gN-RU_A6pSMdzdE4xMzt6Cnc5d_St6sUjaIej2gL9SLyjkrQodc8hYJFhGIMGSBcUVFQ1pEDMXcIC7pvqphasD0K1uCpg_NbWMMXMnkK4fgU_SX2jkorvfOGwj3q9v-qpNMv8i2vcDIfPlaSqW1siJ2klquGH4vuAba6J17rgebhWXgao"
            />
          </a>
        </div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900 via-slate-900/40 to-transparent z-10 pointer-events-none"></div>

        <div className="relative z-20 flex flex-col justify-between p-12 w-full">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={handleBackClick}
          >
            <div className="flex size-10 items-center justify-center rounded bg-primary p-2">
              <Image
                src="/logo-platform-white.svg"
                alt="GeoOffice Platform"
                width={24}
                height={24}
                className="size-6"
              />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-white uppercase">
              GeoOffice <span className="text-primary">Platform</span>
            </h2>
          </div>

          <div>
            <h1 className="text-5xl font-extrabold text-white leading-tight mb-6">
              Precisão em cada <br />
              <span className="text-primary">coordenada.</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-md">
              Soluções avançadas de topografia, cartografia e engenharia para
              profissionais que exigem excelência técnica.
            </p>
          </div>

          <div className="flex gap-4 items-center text-slate-500 text-xs font-mono uppercase tracking-widest">
            <span>SYSTEM v4.2.0</span>
            <span className="w-1 h-1 bg-emerald-500 rounded-full"></span>
            <span>DATA STATUS: ACTIVE</span>
          </div>
        </div>
      </div>

      {/* Right Side: Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center relative grid-pattern topo-lines px-6">
        {/* Mobile Logo */}
        <div
          className="lg:hidden absolute top-10 flex items-center gap-2 cursor-pointer"
          onClick={handleBackClick}
        >
          <div className="flex size-8 items-center justify-center rounded bg-primary p-1.5">
            <Image
              src="/logo-platform-white.svg"
              alt="GeoOffice Platform"
              width={20}
              height={20}
              className="size-5"
            />
          </div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            GeoOffice Platform
          </h2>
        </div>

        <div className="w-full max-w-[440px] z-10">
          <div className="mb-10 text-center lg:text-left">
            <h3 className="text-3xl font-bold text-primary dark:text-white mb-2">
              Login
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              Acesse sua estação de trabalho digital.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                E-mail
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" />
                <input
                  className={`w-full bg-white dark:bg-[#1c2127] border h-14 pl-12 pr-4 rounded transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 outline-none ${
                    fieldErrors.email
                      ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/30"
                      : "border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary focus:ring-1 focus:ring-primary"
                  }`}
                  placeholder="exemplo@geooffice.com"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => ({ ...prev, email: undefined }));
                    }
                  }}
                  disabled={isSubmitting}
                />
              </div>
              {fieldErrors.email && (
                <p className="text-xs text-red-500">{fieldErrors.email}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Senha
                </label>
                <a
                  className="cursor-pointer text-xs font-medium uppercase tracking-wide text-primary dark:text-sky-500 transition-colors hover:text-primary/80 dark:hover:text-sky-400 hover:underline"
                  href="/auth/reset-password/recovery"
                >
                  Esqueceu a senha?
                </a>
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors" />
                <input
                  className={`w-full bg-white dark:bg-[#1c2127] border h-14 pl-12 pr-12 rounded transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 outline-none ${
                    fieldErrors.password
                      ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/30"
                      : "border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary focus:ring-1 focus:ring-primary"
                  }`}
                  placeholder="••••••••"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        password: undefined,
                      }));
                    }
                  }}
                  disabled={isSubmitting}
                />
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <Eye className="size-5" />
                  ) : (
                    <EyeOff className="size-5" />
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-xs text-red-500">{fieldErrors.password}</p>
              )}
            </div>

            {generalError && (
              <p className="text-sm text-red-500">{generalError}</p>
            )}

            <button
              className="w-full bg-primary hover:bg-primary/90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold h-14 rounded transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-2 group uppercase tracking-widest text-sm"
              type="submit"
              disabled={isSubmitting || isLocked}
            >
              <span>
                {isSubmitting
                  ? "Autenticando..."
                  : isLocked
                    ? `Tente novamente em ${lockRemainingSeconds}s`
                    : "Entrar"}
              </span>
              <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="mt-8 text-center border-t border-slate-200 dark:border-slate-800 pt-8">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Não tem uma conta?{" "}
              <button
                onClick={handleSignupClick}
                className="ml-1 cursor-pointer font-bold text-primary dark:text-sky-500 hover:text-primary/80 dark:hover:text-sky-400 hover:underline"
              >
                Cadastre-se
              </button>
            </p>
          </div>

          <div className="mt-12 flex justify-center">
            <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-xs font-medium uppercase">
              <HelpCircle className="size-4" />
              Suporte Técnico
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
